import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { documentAPI, tenantAPI, userAPI } from "../../lib/api";
import { toast } from "sonner";
import { formatDate, capitalizeFirst } from "../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Badge } from "../../components/ui/badge";
import { Plus, FileText, Search, MoreHorizontal, Trash2, Download, Loader2, Lock, Building2, User, UploadCloud, ChevronsUpDown } from "lucide-react";

// Searchable employee picker (by name or employee ID)
const EmployeeCombobox = ({ users, value, onChange, selfLabel }) => {
  const [open, setOpen] = useState(false);
  const selected = users.find((u) => u.id === value);
  const label =
    value && value !== "none" && selected
      ? `${selected.first_name} ${selected.last_name} (${selected.employee_id})`
      : selfLabel;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          className="w-full justify-between font-normal"
          data-testid="document-employee-combobox"
        >
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search by name or employee ID..." data-testid="employee-search-input" />
          <CommandList>
            <CommandEmpty>No employee found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__self__"
                onSelect={() => {
                  onChange("none");
                  setOpen(false);
                }}
                data-testid="employee-option-self"
              >
                {selfLabel}
              </CommandItem>
              {users.map((u) => (
                <CommandItem
                  key={u.id}
                  value={`${u.first_name} ${u.last_name} ${u.employee_id}`}
                  onSelect={() => {
                    onChange(u.id);
                    setOpen(false);
                  }}
                  data-testid={`employee-option-${u.employee_id}`}
                >
                  {u.first_name} {u.last_name} ({u.employee_id})
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const DocumentsPage = () => {
  const { user, isHRAdmin, isSuperAdmin } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    description: "",
    expiry_date: "",
    is_confidential: false,
    tenant_id: "",
    user_id: "",
  });

  const categories = ["identity", "education", "employment", "financial", "medical", "other"];

  const formatSize = (b) => (b == null ? "" : b < 1024 ? `${b} B` : `${(b / 1024).toFixed(0)} KB`);

  useEffect(() => {
    fetchDocuments();
    if (isSuperAdmin) {
      fetchTenants();
    }
    if (isHRAdmin || isSuperAdmin) {
      fetchAllUsers();
    }
  }, [categoryFilter]);

  const fetchAllUsers = async () => {
    try {
      const response = await userAPI.getAll();
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await tenantAPI.getAll();
      setTenants(response.data);
    } catch (error) {
      console.error("Failed to fetch tenants:", error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const params = {};
      if (categoryFilter !== "all") {
        params.category = categoryFilter;
      }
      const response = await documentAPI.getAll(params);
      setDocuments(response.data);
    } catch (error) {
      toast.error("Failed to fetch documents");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error("Please attach a file to upload");
      return;
    }
    if (!formData.category) {
      toast.error("Please select a category");
      return;
    }
    if (isSuperAdmin && !formData.tenant_id) {
      toast.error("Please select a tenant");
      return;
    }
    setUploadLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", selectedFile);
      fd.append("name", formData.name);
      fd.append("category", formData.category);
      fd.append("description", formData.description || "");
      fd.append("expiry_date", formData.expiry_date || "");
      fd.append("is_confidential", formData.is_confidential ? "true" : "false");
      if (isSuperAdmin && formData.tenant_id) {
        fd.append("tenant_id", formData.tenant_id);
      }
      if (formData.user_id && formData.user_id !== "none") {
        fd.append("user_id", formData.user_id);
      }

      await documentAPI.upload(fd);
      toast.success("Document uploaded successfully");
      setShowUploadDialog(false);
      setFormData({
        name: "",
        category: "",
        description: "",
        expiry_date: "",
        is_confidential: false,
        tenant_id: "",
        user_id: "",
      });
      setSelectedFile(null);
      setUsers([]);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await documentAPI.download(doc.id);
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: doc.content_type || "application/octet-stream" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.file_name || doc.name || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  const handleDelete = async (docId) => {
    if (!confirm("Are you sure you want to delete this document?")) return;
    try {
      await documentAPI.delete(docId);
      toast.success("Document deleted");
      fetchDocuments();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const isAdmin = isHRAdmin || isSuperAdmin;

  const empOf = (doc) => users.find((u) => u.id === doc.user_id);
  const empName = (doc) => {
    const e = empOf(doc);
    return e ? `${e.first_name} ${e.last_name}` : "—";
  };
  const empIdOf = (doc) => {
    const e = empOf(doc);
    return e ? e.employee_id : "—";
  };

  const filteredDocuments = documents.filter((doc) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    const e = empOf(doc);
    const hay = [doc.name, doc.category, e?.first_name, e?.last_name, e?.employee_id]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });

  const getCategoryColor = (category) => {
    const colors = {
      identity: "bg-blue-100 text-blue-700",
      education: "bg-emerald-100 text-emerald-700",
      employment: "bg-violet-100 text-violet-700",
      financial: "bg-amber-100 text-amber-700",
      medical: "bg-red-100 text-red-700",
      other: "bg-slate-100 text-slate-700",
    };
    return colors[category] || colors.other;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="documents-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Documents</h1>
          <p className="text-slate-500">Manage and access your documents</p>
        </div>
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="upload-document-btn">
              <Plus className="w-4 h-4 mr-2" />
              Upload Document
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Upload Document</DialogTitle>
              <DialogDescription>Attach a file and fill in the details. Images are compressed automatically.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpload} className="space-y-4 mt-4">
              {/* SuperAdmin: Tenant and Employee Selection */}
              {isSuperAdmin && (
                <>
                  <div className="form-group">
                    <Label className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Tenant
                    </Label>
                    <Select
                      value={formData.tenant_id}
                      onValueChange={(value) => setFormData({ ...formData, tenant_id: value, user_id: "" })}
                    >
                      <SelectTrigger data-testid="document-tenant-select">
                        <SelectValue placeholder="Select tenant" />
                      </SelectTrigger>
                      <SelectContent>
                        {tenants.map((tenant) => (
                          <SelectItem key={tenant.id} value={tenant.tenant_id}>
                            {tenant.company_name} ({tenant.tenant_id})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {formData.tenant_id && (
                    <div className="form-group">
                      <Label className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Employee (Optional)
                      </Label>
                      <EmployeeCombobox
                        users={users.filter((u) => u.tenant_id === formData.tenant_id)}
                        value={formData.user_id}
                        onChange={(val) => setFormData({ ...formData, user_id: val })}
                        selfLabel="All employees in tenant"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Search by name or employee ID. Leave as default to upload for the entire tenant.
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* HR Admin: upload on behalf of an employee */}
              {isHRAdmin && (
                <div className="form-group">
                  <Label className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Employee
                  </Label>
                  <EmployeeCombobox
                    users={users}
                    value={formData.user_id}
                    onChange={(val) => setFormData({ ...formData, user_id: val })}
                    selfLabel="Myself (my own documents)"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Search by name or employee ID to upload on behalf of an employee.
                  </p>
                </div>
              )}

              <div className="form-group">
                <Label>Document Name</Label>
                <Input
                  placeholder="Passport"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="document-name-input"
                />
              </div>
              <div className="form-group">
                <Label>Attach File</Label>
                <label
                  htmlFor="document-file"
                  className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-lg p-6 cursor-pointer hover:border-indigo-400 transition-colors"
                  data-testid="document-file-dropzone"
                >
                  <UploadCloud className="w-6 h-6 text-slate-400" />
                  {selectedFile ? (
                    <span className="text-sm text-slate-700 font-medium text-center break-all">
                      {selectedFile.name} ({formatSize(selectedFile.size)})
                    </span>
                  ) : (
                    <span className="text-sm text-slate-500 text-center">
                      Click to choose a file — images are compressed automatically
                    </span>
                  )}
                  <input
                    id="document-file"
                    type="file"
                    accept="image/*,application/pdf,.doc,.docx,.txt"
                    className="hidden"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    data-testid="document-file-input"
                  />
                </label>
              </div>
              <div className="form-group">
                <Label>Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger data-testid="document-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat} className="capitalize">
                        {capitalizeFirst(cat)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="form-group">
                <Label>Description (Optional)</Label>
                <Input
                  placeholder="Document description..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <div className="form-group">
                <Label>Expiry Date (Optional)</Label>
                <Input
                  type="date"
                  value={formData.expiry_date}
                  onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                />
              </div>
              {isHRAdmin && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="confidential"
                    checked={formData.is_confidential}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_confidential: checked })}
                  />
                  <Label htmlFor="confidential" className="text-sm font-normal">
                    Mark as confidential (HR only)
                  </Label>
                </div>
              )}
              <Button type="submit" className="w-full btn-primary" disabled={uploadLoading} data-testid="submit-document">
                {uploadLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upload Document"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder={isAdmin ? "Search by employee name, ID or document..." : "Search documents..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="document-search-input"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-48" data-testid="category-filter-select">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat} className="capitalize">
                    {capitalizeFirst(cat)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Documents Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                {isAdmin && <TableHead className="w-12">Sr No</TableHead>}
                {isAdmin && <TableHead>Employee ID</TableHead>}
                {isAdmin && <TableHead>Name</TableHead>}
                <TableHead>Document</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Expiry</TableHead>
                <TableHead>Uploaded</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDocuments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 9 : 6} className="text-center py-8 text-slate-500">
                    No documents found
                  </TableCell>
                </TableRow>
              ) : (
                filteredDocuments.map((doc, index) => (
                  <TableRow key={doc.id} data-testid={`document-row-${doc.id}`}>
                    {isAdmin && <TableCell className="text-slate-500">{index + 1}</TableCell>}
                    {isAdmin && <TableCell className="font-medium" data-testid={`document-empid-${doc.id}`}>{empIdOf(doc)}</TableCell>}
                    {isAdmin && <TableCell data-testid={`document-empname-${doc.id}`}>{empName(doc)}</TableCell>}
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                          <span className="font-medium">{doc.name}</span>
                          {doc.is_confidential && (
                            <Lock className="inline-block w-3 h-3 ml-2 text-slate-400" />
                          )}
                          {doc.file_size != null && (
                            <p className="text-xs text-slate-400">{formatSize(doc.file_size)}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getCategoryColor(doc.category)}>
                        {capitalizeFirst(doc.category)}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {doc.description || "-"}
                    </TableCell>
                    <TableCell>
                      {doc.expiry_date ? formatDate(doc.expiry_date) : "-"}
                    </TableCell>
                    <TableCell>{formatDate(doc.created_at)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleDownload(doc)} data-testid={`download-document-${doc.id}`}>
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDelete(doc.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default DocumentsPage;
